import React, { ChangeEvent, forwardRef, RefAttributes, useCallback, useEffect, useRef } from 'react';
import { MixProps } from '@visalex/core';

import cx from 'clsx';

import { BaseFieldProps } from '../../Form/utils/connectFields/connectFields.utils';

import styles from './TextArea.module.scss';

export type Resize = 'both' | 'vertical' | 'horizontal' | 'smart' | 'none';

export type TextAreaProps = MixProps<
  BaseFieldProps<string> & {
    /**
     * Validity of the field: valid=false = invalid, valid=null|undefined = not yet validated
     */
    valid?: boolean | null;

    /**
     * Enables the resizing of the textarea:
     * both: both directions.
     * horizontal: only along the x-axis.
     * vertical: only along the y-axis.
     * smart (default): vertically grows and shrinks the textarea automatically to wrap your input text.
     * none: explicitly disallow resizing on the textarea.
     */
    resize?: Resize;

    heightRefresher?: number | boolean | string;

    onChange?: (newValue: string, e: ChangeEvent<HTMLTextAreaElement>) => void;

    underlined?: boolean;
  } & RefAttributes<HTMLTextAreaElement>,
  HTMLTextAreaElement
>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ onChange, resize = 'smart', heightRefresher, className, style, valid, underlined, ...props }, forwardedRef) => {
    const textareaElementRef = useRef<HTMLTextAreaElement | null>(null);

    const getTextAreaRef = (ref: HTMLTextAreaElement | null) => {
      textareaElementRef.current = ref;

      if (forwardedRef && typeof forwardedRef === 'object') {
        forwardedRef.current = ref;
      }

      if (forwardedRef && typeof forwardedRef === 'function') {
        forwardedRef(ref);
      }
    };

    const resizeTextarea = useCallback(() => {
      if (resize === 'smart') {
        // Used to shrink the textarea
        if (textareaElementRef.current) {
          textareaElementRef.current.style.height = 'auto';
          textareaElementRef.current.style.height = `${textareaElementRef.current?.scrollHeight + 2}px`;
        }
      }
    }, [resize]);

    const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      resizeTextarea();

      if (!props.readOnly && !props.disabled && onChange) {
        onChange(event.target.value, event);
      }
    };

    useEffect(() => {
      textareaElementRef.current && resizeTextarea();
    }, [heightRefresher, textareaElementRef.current]);

    return (
      <textarea
        {...props}
        ref={getTextAreaRef}
        style={{ resize: resize === 'smart' ? 'none' : resize, ...style }}
        className={cx(
          className,
          styles.textarea,
          typeof valid === 'boolean' && styles[`textarea--${valid ? 'valid' : 'invalid'}`],
          underlined && styles['textarea--underlined']
        )}
        onChange={handleOnChange}
        data-testid="textarea-component"
      />
    );
  }
);

TextArea.displayName = 'TextArea';
